/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file ModuleTabBar.jsx
 * @description
 * The **module-scoped floating bottom bar** — the dynamic bar that replaces the
 * app's main tab bar while the user is inside a module. It renders a left-most
 * "menu" exit pill (returns to the main navigation) followed by the module's
 * own tabs, all inside a premium floating rounded surface that mirrors the
 * Management tab-bar etalon (orange active tint, soft shadow, safe-area aware).
 *
 * Purely presentational + theme-driven — the tab model (labels, icons, active
 * flag, onPress, badges, exit handler) comes from {@link module:helpers/useModuleNav}.
 * Responsive: labels shrink to fit and the exit label collapses to an icon on
 * narrow screens, so 3-4 tabs + the exit pill fit every phone width.
 */

import React, {useCallback, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import Icon from '../management/SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';
import {useBottomInset} from '../../helpers/safeArea';
import {withHaptic} from '../../helpers/haptics';

/** Reserve space for the exit pill (icon [+ label]) when sizing tab cells. */
const EXIT_LABEL_MIN_WIDTH = 392;

/**
 * @param {object} props
 * @param {Array<{key:string,label:string,icon:string,active:boolean,badge?:(number|string),onPress:()=>void}>} props.tabs
 * @param {()=>void} props.onExit       Return to the main app navigation.
 * @param {string} [props.menuLabel]    Label for the exit pill (e.g. "Menu").
 * @param {object} [props.style]        Extra style for the outer wrapper.
 * @returns {React.ReactElement|null}
 */
export const ModuleTabBar = ({tabs = [], onExit, menuLabel = 'Menü', style}) => {
    const m = useManagementTheme();
    const bottomInset = useBottomInset();
    const [barWidth, setBarWidth] = useState(0);

    const onLayout = useCallback((e) => setBarWidth(e.nativeEvent.layout.width), []);
    const showExitLabel = barWidth === 0 || barWidth >= EXIT_LABEL_MIN_WIDTH;

    if (!Array.isArray(tabs) || tabs.length === 0) {
        return null;
    }

    return (
        <View pointerEvents="box-none" style={[styles.wrap, {bottom: 10 + bottomInset}, style]}>
            <View
                onLayout={onLayout}
                style={[styles.bar, {backgroundColor: m.colors.surface, borderColor: m.colors.border}, m.shadow(3)]}
            >
                {/* Exit / back-to-main pill */}
                <TouchableOpacity
                    onPress={onExit}
                    activeOpacity={0.85}
                    accessibilityRole="button"
                    accessibilityLabel={menuLabel}
                    style={[styles.exit, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}, showExitLabel ? styles.exitWide : styles.exitCompact]}
                >
                    <Icon name="chevron-left" size={20} color={m.colors.heading} />
                    {showExitLabel && (
                        <Text style={[styles.exitText, {color: m.colors.heading}]} numberOfLines={1}>{menuLabel}</Text>
                    )}
                </TouchableOpacity>

                <View style={[styles.divider, {backgroundColor: m.colors.border}]} />

                {/* Module tabs */}
                {tabs.map((tab) => {
                    const active = !!tab.active;
                    const tint = active ? m.colors.primary : m.colors.subText;
                    const hasBadge = tab.badge != null && tab.badge !== 0 && tab.badge !== '';

                    return (
                        <TouchableOpacity
                            key={tab.key}
                            onPress={active ? tab.onPress : withHaptic(tab.onPress, 'select')}
                            activeOpacity={0.8}
                            accessibilityRole="tab"
                            accessibilityState={{selected: active}}
                            accessibilityLabel={tab.label}
                            style={styles.tab}
                        >
                            <View style={[styles.tabInner, active && {backgroundColor: m.colors.chipBg}]}>
                                <View>
                                    <Icon name={tab.icon} size={21} color={active ? '#B77400' : tint} />
                                    {hasBadge && (
                                        <View style={styles.badge}>
                                            <Text style={styles.badgeText} numberOfLines={1}>
                                                {typeof tab.badge === 'number' && tab.badge > 99 ? '99+' : tab.badge}
                                            </Text>
                                        </View>
                                    )}
                                </View>
                            </View>
                            <Text
                                style={[styles.tabLabel, {color: active ? m.colors.heading : m.colors.subText, fontWeight: active ? '800' : '600'}]}
                                numberOfLines={1}
                                allowFontScaling={false}
                            >
                                {tab.label}
                            </Text>
                        </TouchableOpacity>
                    );
                })}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    wrap: {position: 'absolute', left: 10, right: 10},
    bar: {
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1,
        borderRadius: 24,
        paddingHorizontal: 6,
        paddingVertical: 7,
        minHeight: 62,
    },
    exit: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1,
        borderRadius: 16,
        height: 46,
        gap: 3,
    },
    exitWide: {paddingHorizontal: 12},
    exitCompact: {width: 44},
    exitText: {fontSize: 12.5, fontWeight: '800'},
    divider: {width: 1, height: 30, marginHorizontal: 5, borderRadius: 1},
    tab: {flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 2},
    tabInner: {alignItems: 'center', justifyContent: 'center', width: 46, height: 32, borderRadius: 12, marginBottom: 3},
    tabLabel: {fontSize: 10.5, textAlign: 'center', letterSpacing: 0.1, paddingHorizontal: 2},
    badge: {
        position: 'absolute',
        top: -6,
        right: -9,
        minWidth: 17,
        height: 17,
        borderRadius: 8.5,
        backgroundColor: '#E53935',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 3,
        borderWidth: 1.5,
        borderColor: '#FFFFFF',
    },
    badgeText: {color: '#FFFFFF', fontSize: 9.5, fontWeight: '900', lineHeight: 12},
});

export default ModuleTabBar;
